import { CustomObjectService } from "../../services/customObject.service";
import {
  AfterViewInit,
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy
} from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { RoutePaths } from "src/app/shared/constants/routePaths.constants";
import { CustomObjectTypeEnum } from "src/app/enums/customObjectType.enum";
import { CustomObjectLookup } from "src/app/models/customObjectLookup.model";
import { DateFilterTypeEnum } from "../../enums/dateFilterType.enum";
import { AuthService } from "src/app/services";
import { CommonService } from "src/app/services/common.service";
import { UserPreferencesService } from "src/app/services/userPreferences.service";
import { sidebarPath } from "src/app/shared/constants/sidebarPath.constants";
import { OrganizationLevelPermissions } from "src/app/models/organizationLevelPermissions";
import { MenuItem } from "src/app/models/menuItem.model";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { MENU_ITEMS } from "../../shared/constants/menu.constants";
import { PermissionService } from "src/app/services/permission.service";

import { trackByIndex } from "src/app/shared/utils/track-by.util";
@Component({
  standalone: false,
    selector: "app-sidenav",
    templateUrl: "./sidenav.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./sidenav.component.scss"],
})
export class SidenavComponent implements OnInit, AfterViewInit, OnDestroy {
    readonly DEFAULT_END_POINT_ICON = "fas fa-desktop";
    @Input() isCollapsed: boolean = true;
    coId: string | number;
    defaultCoId: string | number;
    employeeId: string | number;
    endPointName: string;
    endPointIcon: string;
    nodes: CustomObjectLookup[] = [];
    organizationLevelPermissions: OrganizationLevelPermissions;
    menuItems: MenuItem[] = [];
    activeRoutes: { [key: string]: boolean } = {};
    private readonly destroy$ = new Subject<void>();
    private urlSubscriptionTimer: ReturnType<typeof setTimeout>;

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private authService: AuthService,
        private customObjectService: CustomObjectService,
        private commonService: CommonService,
        private userPreferencesService: UserPreferencesService,
        private permissionService: PermissionService,
    ) {
        const currentUser = authService.getCurrentUser();
        this.coId = currentUser.preferences?.endPointId;
    }

    ngOnInit(): void {
        const currentUser = this.authService.getCurrentUser();
        this.organizationLevelPermissions =
            currentUser.organizationLevelPermissions;
        const filterBy = JSON.stringify({
            type: `${CustomObjectTypeEnum.EmployeeEndPoint},${CustomObjectTypeEnum.EndPoint},${CustomObjectTypeEnum.LocationEndPoint}`,
        });
        this.customObjectService.getAll(filterBy)
            .pipe(takeUntil(this.destroy$))
            .subscribe((result: CustomObjectLookup[]) => {
                this.nodes = result;
                this.employeeId = result.find(
                    (x) => x.type === CustomObjectTypeEnum.EmployeeEndPoint,
                )?.id;
                const isCoIdValid = result.some((n) => n.id === this.coId);
                this.defaultCoId = isCoIdValid ? this.coId : result[0]?.id;
                this.setMenuItems();
                this.updateActiveRoutes();
            });

        this.router.events.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.updateActiveRoutes();
        });
    }

    ngAfterViewInit(): void {
        this.urlSubscriptionTimer = setTimeout(() => {
            this.activatedRoute.url
                .pipe(takeUntil(this.destroy$))
                .subscribe((data) => {
                    this.setDateForRoute(data[0]?.path || "");
                });
        }, 0);
    }

    ngOnDestroy(): void {
        clearTimeout(this.urlSubscriptionTimer);
        this.destroy$.next();
        this.destroy$.complete();
    }

    setMenuItems(): void {
        const menuItemCoId =
            this.defaultCoId === this.employeeId
                ? this.nodes.find((x) => x.id !== this.employeeId)?.id ||
                  this.defaultCoId
                : this.defaultCoId;

        const allItems = MENU_ITEMS(
            this.organizationLevelPermissions,
            this.employeeId,
            menuItemCoId,
        );
        this.menuItems = this.filterMenuItems(allItems);
    }

    filterMenuItems(items: MenuItem[]): MenuItem[] {
        const result: MenuItem[] = [];
        let sectionBuffer: MenuItem[] = [];

        for (const item of items) {
            if (item.isSeparator) {
                if (sectionBuffer.some((i) => !i.isSeparator && i.permission))
                    result.push(...sectionBuffer);
                sectionBuffer = [item];
                continue;
            }

            const filteredChildren = item.children?.filter(child =>
                child.permission
                && (!child.objectId || this.permissionService.hasReadPermission(child.objectId))
                && (!child.objectIds || this.permissionService.hasAnyReadPermission(child.objectIds))
            );
            const visible =
                item.permission &&
                (!item.objectId ||
                    this.permissionService.hasReadPermission(item.objectId)) &&
                (!item.objectIds ||
                    this.permissionService.hasAnyReadPermission(
                        item.objectIds,
                    ))
                && (!item.children || (filteredChildren?.length ?? 0) > 0);
            if (visible) {
                sectionBuffer.push({ ...item, children: filteredChildren });
            } else {
                sectionBuffer.push({ ...item, permission: false });
            }
        }

        if (sectionBuffer.some((i) => !i.isSeparator && i.permission)) {
            result.push(...sectionBuffer);
        }

        return result;
    }

    toggleCollapse(): void {
        this.isCollapsed = !this.isCollapsed;
    }

    switchEndPoint(endPointId: string): void {
        this.coId = endPointId;
        this.router.navigate([RoutePaths.nodes, endPointId]);
        this.userPreferencesService.setEndPointId(endPointId).subscribe();
    }

    setEndPointName(): void {
        const node = this.nodes.find((x) => x.id === this.coId);
        this.endPointName = node?.name ?? "Category";
        this.endPointIcon = node?.icon ?? this.DEFAULT_END_POINT_ICON;
    }

    setDateForRoute(path: string): void {
        switch (path) {
            case sidebarPath.devices:
            case sidebarPath.nodes:
                this.dateEvent(
                    DateFilterTypeEnum.Today,
                    this.commonService.getEnumName(
                        DateFilterTypeEnum,
                        DateFilterTypeEnum.Today,
                    ),
                );
                break;
            case sidebarPath.real_time_dashboard:
                this.dateEvent(
                    DateFilterTypeEnum.Today,
                    this.commonService.getEnumName(
                        DateFilterTypeEnum,
                        DateFilterTypeEnum.Today,
                    ),
                );
                this.commonService.liveOptionSubject.next({
                    show: true,
                    enabled: true,
                });
                break;
            case sidebarPath.dashboard:
                this.dateEvent(
                    DateFilterTypeEnum.ThisYear,
                    this.commonService.getEnumName(
                        DateFilterTypeEnum,
                        DateFilterTypeEnum.ThisYear,
                    ),
                );
                break;
            case sidebarPath.samples:
            case sidebarPath.segs:
                if (
                    this.commonService.sidebarPath ===
                    sidebarPath.real_time_dashboard
                ) {
                    this.dateEvent(
                        DateFilterTypeEnum.ThisYear,
                        this.commonService.getEnumName(
                            DateFilterTypeEnum,
                            DateFilterTypeEnum.ThisYear,
                        ),
                    );
                }
                break;
            default:
                break;
        }

        this.commonService.sidebarPath = path;
    }

    dateEvent(type: DateFilterTypeEnum, name: string): void {
        this.commonService.selectedDateFilter({ id: type, value: name });
    }

    updateActiveRoutes(): void {
        const currentUrl = this.router.url;
        this.menuItems.forEach((item) => {
            if (item.route) {
                this.activeRoutes[item.route] = this.checkRouteActive(
                    item.route,
                    currentUrl,
                );
            }
            item.children?.forEach(child => {
                if (child.route) {
                    this.activeRoutes[child.route] = this.checkRouteActive(
                        child.route,
                        currentUrl,
                    );
                }
            });
            if (item.children?.length) {
                const hasActiveChild = item.children.some(child =>
                    !!child.route && this.checkRouteActive(child.route, currentUrl)
                );
                item.expanded = hasActiveChild || !!item.expanded;
            }
        });
    }

    checkRouteActive(route: string, currentUrl: string): boolean {
        if (!route) return false;

        const id = route.split("/").pop();
        const isWorkforce = route.startsWith("/dashboard/workforce/");
        const isNode = route.startsWith("/dashboard/nodes/");

        if (route === "/dashboard/") return currentUrl === "/dashboard";
        if (isWorkforce && id)
            return id === String(this.employeeId) && currentUrl.endsWith(id);
        if (isNode && id)
            return (
                id !== String(this.employeeId) &&
                currentUrl.startsWith("/dashboard/nodes/") &&
                currentUrl.endsWith(id)
            );

        const urlPath = currentUrl.split(/[?#]/)[0];
        return urlPath === route || urlPath.startsWith(route + "/");
    }

    isRouteActive(item: MenuItem): boolean {
        return this.activeRoutes[item.route];
    }

    isAssetPath(icon: string): boolean {
        // Check if icon is a file path (SVG asset or other asset)
        return /^(\/|\.\.\/|http|\.\/|assets)/.test(icon);
    }

    toggleMenuExpand(item: MenuItem): void {
        item.expanded = !item.expanded;
    }

    public trackByIndex = trackByIndex;
}
